import axios, {
  Axios,
  AxiosRequestConfig,
  Canceler,
  AxiosResponse,
} from "axios";
import { CUSTOM_CONFIG_KEY } from "./customConfig";
import { InterceptorManager } from "./InterceptorManager";
import { HttpBase } from "./HttpBase";

export interface ResponseSuccess<T> {
  Code: number;
  Message: string;
  Data?: T | T[];
}

export class ResponseError {
  readonly Code!: number;
  readonly Message!: string;
  readonly Data?: {
    ErrKey?: string;
    ErrValue?: string[];
  };

  constructor(error: ResponseError) {
    this.Code = error.Code;
    this.Message = error.Message;
    this.Data = error.Data;
  }
}
export type ResponseData<T = any> = ResponseSuccess<T> | ResponseError;

export type CancelCallback = (cancel: Canceler) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type HttpClientDefaults = Omit<AxiosRequestConfig<any>, "headers">;

export class HttpClient<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CUSTOM_CONFIG extends Record<string, any>
> extends HttpBase {
  private readonly _axiosInstance: Axios;
  private _hasBoundInterceptors = false;

  public defaultConfig: HttpClientDefaults = { timeout: 4000 };
  public readonly client: Omit<Axios, "interceptors" | "defaults">;
  public readonly interceptors: {
    readonly request: InterceptorManager<AxiosRequestConfig>;
    readonly response: InterceptorManager<AxiosResponse>;
  };

  constructor(defaultConfig: HttpClientDefaults = {}) {
    super();
    this.defaultConfig = defaultConfig;
    const axiosInstance = axios.create(defaultConfig);
    this._axiosInstance = axiosInstance;
    this.client = axiosInstance;
    this.interceptors = {
      request: new InterceptorManager(),
      response: new InterceptorManager(),
    };
  }

  updateDefaultConfig(
    config: HttpClientDefaults,
    updateStrategy: "onlyAdd" | "addAndUpdate" | "cover" = "onlyAdd"
  ) {
    if (updateStrategy === "onlyAdd") {
      this.defaultConfig = { ...config, ...this.defaultConfig };
    } else if (updateStrategy === "addAndUpdate") {
      this.defaultConfig = { ...this.defaultConfig, ...config };
    } else if (updateStrategy === "cover") {
      this.defaultConfig = { ...config };
    }
    const configKeys = Object.keys(this.defaultConfig) as Array<
      keyof HttpClientDefaults
    >;
    configKeys.map((key) => {
      this._axiosInstance.defaults[key] = this.defaultConfig[key];
    });
    return this;
  }

  bindInterceptor() {
    if (this._hasBoundInterceptors) {
      throw new Error("Client has bound interceptors. Cannot bind again.");
    }

    this._hasBoundInterceptors = true;
    this._axiosInstance.interceptors.request.use(
      function deleteCustomConfig(config: AxiosRequestConfig) {
        const newConfig = { ...config };
        if (newConfig.headers) {
          delete newConfig.headers[CUSTOM_CONFIG_KEY];
        }
        return newConfig;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    const requestInterceptors = this.interceptors.request.getAll();
    for (let i = requestInterceptors.length - 1; i >= 0; i--) {
      const { fulfilled, rejected } = requestInterceptors[i];
      this._axiosInstance.interceptors.request.use(fulfilled, rejected);
    }
    this.interceptors.response.getAll().map(({ fulfilled, rejected }) => {
      this._axiosInstance.interceptors.response.use(fulfilled, rejected);
    });

    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async request<RESPONSE_DATA = any, REQUEST_DATA = any>(
    requestConfig: AxiosRequestConfig,
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const source = axios.CancelToken.source();
    const headers = { ...(requestConfig.headers ?? {}) };
    if (customConfig) {
      headers[CUSTOM_CONFIG_KEY] = JSON.stringify(customConfig);
    }
    const config: AxiosRequestConfig = {
      ...requestConfig,
      cancelToken: source.token,
      headers: headers,
    };

    cancelCallback && cancelCallback(source.cancel);
    const result = await this.client.request<
      ResponseData<RESPONSE_DATA>,
      AxiosResponse<ResponseData<RESPONSE_DATA>>,
      REQUEST_DATA
    >(config);
    if (!result) return Promise.reject();
    if (result && result.data.Code === 0)
      return Promise.resolve(result.data.Data as RESPONSE_DATA);
    const error = new ResponseError(result.data as ResponseError);
    return Promise.reject(error);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<RESPONSE_DATA = any>(
    url: string,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "get",
    };
    return this.request<RESPONSE_DATA>(cfg, customConfig, cancelCallback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<RESPONSE_DATA = any>(
    url: string,
    data?: unknown,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "delete",
      data: data,
    };
    return this.request<RESPONSE_DATA>(cfg, customConfig, cancelCallback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  head<RESPONSE_DATA = any>(
    url: string,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "head",
    };
    return this.request<RESPONSE_DATA>(cfg, customConfig, cancelCallback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options<RESPONSE_DATA = any>(
    url: string,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "options",
    };
    return this.request<RESPONSE_DATA>(cfg, customConfig, cancelCallback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<RESPONSE_DATA = any, REQUEST_DATA = any>(
    url: string,
    data?: unknown,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "post",
      data: data,
    };
    return this.request<RESPONSE_DATA, REQUEST_DATA>(
      cfg,
      customConfig,
      cancelCallback
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<RESPONSE_DATA = any>(
    url: string,
    data?: unknown,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "put",
      data: data,
    };
    return this.request<RESPONSE_DATA>(cfg, customConfig, cancelCallback);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<RESPONSE_DATA = any>(
    url: string,
    data?: unknown,
    requestConfig: AxiosRequestConfig = {},
    customConfig?: CUSTOM_CONFIG,
    cancelCallback?: CancelCallback
  ) {
    const cfg: AxiosRequestConfig = {
      ...requestConfig,
      url: url,
      method: "patch",
      data: data,
    };
    return this.request<RESPONSE_DATA>(cfg, customConfig, cancelCallback);
  }
}
